import React from 'react';
import { Phone } from 'lucide-react';
import { useIsMobile } from '../hooks/useMediaQuery';

interface PhoneLinkProps {
  phone: string;
  showIcon?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Componente que exibe um número de telefone como link clicável em dispositivos móveis
 * Em mobile, ao clicar, abre o aplicativo de discagem
 * Em desktop, apenas exibe o número formatado
 */
const PhoneLink: React.FC<PhoneLinkProps> = ({ 
  phone, 
  showIcon = false,
  className = '',
  style = {}
}) => {
  const isMobile = useIsMobile();

  // Limpar e formatar o número de telefone
  const formatPhone = (phoneNumber: string): string => {
    if (!phoneNumber) return '-';
    
    // Remove todos os caracteres não numéricos
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Formata baseado no tamanho
    if (cleaned.length === 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11) {
      // Celular: (XX) XXXXX-XXXX
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length > 11) {
      // Número internacional ou com código de país
      return phoneNumber; // Mantém formato original
    }
    
    // Se não conseguir formatar, retorna o original
    return phoneNumber;
  };

  // Criar link tel: apenas com números
  const getTelLink = (phoneNumber: string): string => {
    if (!phoneNumber || phoneNumber === '-') return '#';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (!cleaned || cleaned.length < 10) return '#';
    
    // Se já começar com código de país (55), não adiciona novamente
    if (cleaned.startsWith('55') && cleaned.length >= 12) {
      return `tel:+${cleaned}`;
    }
    
    // Adiciona código do Brasil (55) se não tiver
    return `tel:+55${cleaned}`;
  };

  const formattedPhone = formatPhone(phone);
  const telLink = getTelLink(phone);

  // Se não tiver telefone válido, apenas exibe
  if (!phone || phone === '-' || telLink === '#') {
    return (
      <span className={className} style={style}>
        {showIcon && <Phone size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
        {formattedPhone}
      </span>
    );
  }

  // Sempre cria link clicável (funciona em mobile e desktop com apps de telefone)
  return (
    <a
      href={telLink}
      className={className}
      style={{
        color: '#2563eb',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        cursor: 'pointer',
        touchAction: 'manipulation',
        minHeight: '44px',
        minWidth: '44px',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        ...style
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#eff6ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      onClick={(e) => {
        // Em mobile, o link tel: abre automaticamente o app de discagem
        // Em desktop, pode abrir aplicativos como Skype, Teams, etc. se instalados
        // Não prevenir comportamento padrão para permitir ação nativa
      }}
    >
      {showIcon && <Phone size={14} />}
      {formattedPhone}
    </a>
  );
};

export default PhoneLink;

